import { istek } from './istemci';
import type { Semalar } from './sema';
import type { Kimlik } from './tipler';

/**
 * ATAMA UÇLARI.
 *
 * ATAMA SİSTEMİN OMURGASIDIR: yetki pozisyondan, kapsam birimden gelir ve
 * ikisi de atama üzerinden çözülür. Puantaj, izin, görev — hepsi `atamaId`
 * ile kayıtlanır, `personelId` ile değil. Gerekçe: aynı kişi birim
 * değiştirdiğinde geçmiş kayıtlar ESKİ birimde kalmalı; personel kimliğine
 * bağlanmış olsaydı tarihsel raporlar yeni birime kayardı.
 *
 * GÖREVLER AYRILIĞI SUNUCUDA DENETLENİR: kendi atamasını talep eden 400
 * alır (`atama_kendine_talep_chk` ayrıca veritabanında da var). Panel bunu
 * önceden söylemeye çalışmaz — kural sunucunun.
 */

export type AtamaOzeti = Semalar['AtamaOzetiDto'];
export type AtamaSayfasi = Semalar['AtamaSayfasiDto'];
export type KuralDenetimi = Semalar['KuralDenetimiDto'];
export type AtamaTalepSonucu = Semalar['AtamaTalepSonucuDto'];

export type AtamaDurumu = AtamaOzeti['durum'];

export interface AtamaSorgusu {
  personelId?: Kimlik;
  birimId?: Kimlik;
  pozisyonId?: number;
  durum?: AtamaDurumu;
  /** `true` → yalnız BUGÜN geçerli olanlar (`gecerlilik @> CURRENT_DATE`). */
  gecerliMi?: boolean;
  sayfa?: number;
  sayfaBoyutu?: number;
}

export function atamaListesi(sorgu: AtamaSorgusu = {}): Promise<AtamaSayfasi> {
  return istek<AtamaSayfasi>('/v1/atama', { sorgu: { ...sorgu } });
}

export function atamaAyrintisi(atamaId: Kimlik): Promise<AtamaOzeti> {
  return istek<AtamaOzeti>(`/v1/atama/${atamaId}`);
}

/**
 * Bir personelin BUGÜN GEÇERLİ birincil ataması.
 *
 * Puantaj çizelgesi `atamaId` ile açılıyor ama `PersonelOzetiDto` bu alanı
 * TAŞIMIYOR — personel listesi atamadan bağımsız. Bu yardımcı köprüyü kurar.
 *
 * BİRİNCİL ATAMA seçilir: vekâlet geçicidir ve puantaj asıl görev üzerinden
 * tutulur. Birincil yoksa ilk geçerli atama alınır.
 */
export async function personelinAtamasi(personelId: Kimlik): Promise<AtamaOzeti | null> {
  const sayfa = await atamaListesi({ personelId, gecerliMi: true, sayfaBoyutu: 20 });
  const kayitlar = sayfa.kayitlar ?? [];

  return kayitlar.find((a) => a.birincilMi) ?? kayitlar[0] ?? null;
}

/* ------------------------------------------------------------------ */
/* Onay akışı                                                          */
/* ------------------------------------------------------------------ */

/**
 * ONAY BEKLEYEN ATAMALAR — yalnız `ATAMA_ONAYLA`.
 *
 * `ATAMA_YAP` talep eder, `ATAMA_ONAYLA` karara bağlar. İkisi ayrı kod
 * çünkü görevler ayrılığı: atamayı isteyen kişi onaylayamaz. Sunucu ayrıca
 * `atama_kendine_talep_chk` ile kendi atamasını talep etmeyi de engelliyor.
 */
export function onayBekleyenAtamalar(): Promise<AtamaOzeti[]> {
  return istek<AtamaOzeti[]>('/v1/atama/onay-bekleyenler');
}

/** `aciklama` İSTEĞE BAĞLI (`OnayDto`) — reddin aksine. */
export function atamaOnayla(atamaId: Kimlik, aciklama?: string): Promise<void> {
  return istek<void>(`/v1/atama/${atamaId}/onayla`, {
    yontem: 'PATCH',
    govde: { aciklama },
  });
}

/** Gerekçe ZORUNLU: talep eden neden reddedildiğini bilmeli. */
export function atamaReddet(atamaId: Kimlik, gerekce: string): Promise<void> {
  return istek<void>(`/v1/atama/${atamaId}/reddet`, {
    yontem: 'PATCH',
    govde: { gerekce },
  });
}

/**
 * Atamayı sonlandırır. ATAMA SİLİNMEZ — bitiş tarihi yazılır.
 *
 * `bitisTarihi` ZORUNLU ve bugün olmak zorunda değil: görevden ayrılma
 * tarihi geriye dönük de olabilir. Silinseydi o tarihe kadarki puantaj
 * kayıtları sahipsiz kalırdı.
 */
export function atamaSonlandir(
  atamaId: Kimlik,
  bitisTarihi: string,
  gerekce?: string,
): Promise<void> {
  return istek<void>(`/v1/atama/${atamaId}/sonlandir`, {
    yontem: 'PATCH',
    govde: { bitisTarihi, gerekce },
  });
}
